import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useActionsAudit, useAgents, useAudit, useVerificationAudit } from '@/api/hooks';
import type { VerificationAudit } from '@/api/types';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateHeureFr } from '@/lib/utils';
import { OngletsAdministration } from './Onglets';

const LIMITE = 50;

export function JournalAudit() {
  const [filtre, setFiltre] = useState({ action: '', objetType: '', utilisateurId: '', depuis: '', jusqua: '' });
  const [page, setPage] = useState(1);
  const actions = useActionsAudit();
  const agents = useAgents();
  const verification = useVerificationAudit();
  const [resultat, setResultat] = useState<VerificationAudit | null>(null);
  const { data, isLoading, error } = useAudit({ action: filtre.action || undefined, objetType: filtre.objetType.trim() || undefined, utilisateurId: filtre.utilisateurId || undefined, depuis: filtre.depuis || undefined, jusqua: filtre.jusqua || undefined, page, limite: LIMITE });
  const maj = (patch: Partial<typeof filtre>) => {
    setFiltre({ ...filtre, ...patch });
    setPage(1);
  };
  const verifier = async () => {
    try {
      setResultat(await verification.mutateAsync());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Vérification impossible.');
    }
  };
  const pages = data ? Math.max(1, Math.ceil(data.total / LIMITE)) : 1;

  return (
    <div>
      <PageHeader
        titre="Administration"
        sousTitre="Journal chaîné et infalsifiable de toutes les actions : connexions, décisions, modifications, pièces, paramètres. Chaque entrée porte l’empreinte de la précédente."
        actions={
          <Button variant="outline" onClick={verifier} disabled={verification.isPending}>
            <ShieldCheck className="size-4" /> Vérifier la chaîne
          </Button>
        }
      />
      <OngletsAdministration />
      {resultat && (
        <Alert variant={resultat.ok ? 'default' : 'destructive'} className="mb-4">
          <AlertTitle>{resultat.ok ? 'Chaîne intègre' : 'Altération détectée'}</AlertTitle>
          <AlertDescription>
            {resultat.nombre} événement(s) vérifié(s){resultat.premierEcart ? ` · premier écart à l’événement n° ${resultat.premierEcart}` : ''}{resultat.ancreCoherente ? '' : ' · ancre incohérente'}.
          </AlertDescription>
        </Alert>
      )}
      <div className="mb-4 grid gap-2 md:grid-cols-5">
        <SelectNatif value={filtre.action} onChange={(e) => maj({ action: e.target.value })} aria-label="Action">
          <option value="">Toutes les actions</option>
          {(actions.data ?? []).map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </SelectNatif>
        <SelectNatif value={filtre.utilisateurId} onChange={(e) => maj({ utilisateurId: e.target.value })} aria-label="Utilisateur">
          <option value="">Tous les utilisateurs</option>
          {(agents.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.prenom} {a.nom}
            </option>
          ))}
        </SelectNatif>
        <Input placeholder="Type d’objet (TDR, FED, COURRIER…)" value={filtre.objetType} onChange={(e) => maj({ objetType: e.target.value.toUpperCase() })} />
        <Input type="date" value={filtre.depuis} onChange={(e) => maj({ depuis: e.target.value })} aria-label="Depuis" />
        <Input type="date" value={filtre.jusqua} onChange={(e) => maj({ jusqua: e.target.value })} aria-label="Jusqu’au" />
      </div>
      {isLoading ? (
        <Chargement />
      ) : error || !data ? (
        <ErreurBloc erreur={error} />
      ) : data.evenements.length === 0 ? (
        <Vide message="Aucun événement pour ces critères." />
      ) : (
        <>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N°</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead className="hidden md:table-cell">Objet</TableHead>
                  <TableHead className="hidden lg:table-cell">Détail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.evenements.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="num text-muted-foreground">{e.seq}</TableCell>
                    <TableCell className="whitespace-nowrap">{dateHeureFr(e.horodatage)}</TableCell>
                    <TableCell>
                      {e.utilisateurNom ?? e.utilisateurId}
                      {e.titulaireNom && <div className="text-xs text-muted-foreground">pour {e.titulaireNom}</div>}
                      {e.adresseIp && <div className="text-xs text-muted-foreground">{e.adresseIp}</div>}
                    </TableCell>
                    <TableCell className="font-medium">{e.action}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {e.objetType}
                      <div className="max-w-[16ch] truncate text-xs text-muted-foreground" title={e.objetId}>{e.objetId}</div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {e.detail && (
                        <details className="text-xs">
                          <summary className="cursor-pointer truncate text-muted-foreground">{Object.keys(e.detail).join(', ')}</summary>
                          <pre className="mt-1 max-w-md whitespace-pre-wrap break-all rounded bg-muted p-2">{JSON.stringify(e.detail, null, 1)}</pre>
                        </details>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {data.total} événement(s) · page {data.page} sur {pages}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                Précédente
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>
                Suivante
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
